/**
 * LeverPanel Component
 * WP-RETIRE-12: Explore changes to a retirement plan without committing them.
 *
 * The reporter asked for "levers or a way to tune things with the numbers" and
 * for a view of "whenever I change any one number, how it would affect
 * things". Previously every assumption had to be SAVED before its effect could
 * be seen, so there was no way to ask "what if?" without altering the plan.
 *
 * This holds a LOCAL DRAFT of the inputs. Moving a lever recalculates entirely
 * client-side — no request, no persisted change — until the user explicitly
 * chooses "Set my plan based on this scenario", which makes the scenario their
 * saved plan. "Reset to my plan" restores the saved values.
 *
 * WP-PLAN-2: the chart shows projected NET WORTH, and the page renders this
 * panel in its own "Explore scenarios" region, apart from the plan regions.
 *
 * The draft is intentionally in-memory only: it is not written to
 * localStorage, which would put the user's financial figures on disk outside
 * the encrypted backend.
 */
import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import { centsToDollars, dollarsToCents, formatCurrency } from '@/utils/format';
import { rankLevers } from '@/utils/sensitivityAnalysis';
import TrajectoryChart from './TrajectoryChart';
import type {
  RetirementEvent,
  RetirementInputsUpdate,
  RetirementPlanningInputs,
} from '@/types/retirement';

export interface LeverPanelProps {
  /** The user's saved plan. */
  inputs: RetirementPlanningInputs;
  netWorthCents: number;
  events?: RetirementEvent[];
  birthYear?: number;
  /** Persists the draft. Only called from the explicit save action. */
  onSave: (update: RetirementInputsUpdate) => void;
  isSaving?: boolean;
  className?: string;
}

/** Debounce so dragging a slider does not recompute on every pixel. */
const RECALC_DEBOUNCE_MS = 150;

export default function LeverPanel({
  inputs,
  netWorthCents,
  events,
  birthYear,
  onSave,
  isSaving = false,
  className,
}: LeverPanelProps) {
  const [draft, setDraft] = useState<RetirementPlanningInputs>(inputs);
  const [debounced, setDebounced] = useState<RetirementPlanningInputs>(inputs);

  // Re-seed when the saved plan changes underneath us (e.g. after a save)
  useEffect(() => {
    setDraft(inputs);
    setDebounced(inputs);
  }, [inputs]);

  // buildProjectionGrid evaluates every age milestone against every return
  // rate; at drag frequency that is wasted work.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(draft), RECALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const isDirty = useMemo(
    () =>
      draft.retirementAge !== inputs.retirementAge ||
      draft.retirementExpensesAnnualCents !==
        inputs.retirementExpensesAnnualCents ||
      draft.currentSavingsAnnualCents !== inputs.currentSavingsAnnualCents,
    [draft, inputs]
  );

  const ranked = useMemo(
    () => rankLevers(debounced, netWorthCents, events, birthYear),
    [debounced, netWorthCents, events, birthYear]
  );

  const patch = (over: Partial<RetirementPlanningInputs>) =>
    setDraft((prev) => ({ ...prev, ...over }));

  const handleSave = () => {
    onSave({
      currentAge: draft.currentAge,
      retirementAge: draft.retirementAge,
      retirementExpensesAnnualCents: draft.retirementExpensesAnnualCents,
      currentSavingsAnnualCents: draft.currentSavingsAnnualCents,
      socialSecurityAnnualCents: draft.socialSecurityAnnualCents,
      pensionAnnualCents: draft.pensionAnnualCents,
      otherRetirementIncomeCents: draft.otherRetirementIncomeCents,
      netWorthAdjustmentCents: draft.netWorthAdjustmentCents,
      socialSecurityStartAge: draft.socialSecurityStartAge,
      pensionStartAge: draft.pensionStartAge,
      otherIncomeStartAge: draft.otherIncomeStartAge,
      workingIncomeAnnualCents: draft.workingIncomeAnnualCents,
    });
  };

  return (
    <div
      className={className}
      data-testid="lever-panel"
      aria-label="Explore scenarios"
    >
      {/* WP-PLAN-2: the page owns this region's <h2> ("Explore scenarios")
          and its sandbox framing, so the panel carries no title of its own. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm text-gray-600">
          Move a lever to see its effect.
        </p>
        {isDirty && (
          <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
            Changed from your plan
          </span>
        )}
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Retirement age: <span className="tabular-nums">{draft.retirementAge}</span>
          </span>
          <input
            type="range"
            min={Math.max(draft.currentAge + 1, 50)}
            max={80}
            value={draft.retirementAge}
            onChange={(e) => patch({ retirementAge: Number(e.target.value) })}
            className="w-full"
            aria-label="Retirement age"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Annual spending in retirement:{' '}
            <span className="tabular-nums">
              {formatCurrency(centsToDollars(draft.retirementExpensesAnnualCents))}
            </span>
          </span>
          <input
            type="range"
            min={0}
            max={Math.max(
              20_000_000,
              Math.round(inputs.retirementExpensesAnnualCents * 2)
            )}
            step={100_000}
            value={draft.retirementExpensesAnnualCents}
            onChange={(e) =>
              patch({ retirementExpensesAnnualCents: Number(e.target.value) })
            }
            className="w-full"
            aria-label="Annual spending in retirement"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Annual savings:{' '}
            <span className="tabular-nums">
              {formatCurrency(centsToDollars(draft.currentSavingsAnnualCents))}
            </span>
          </span>
          <input
            type="range"
            min={0}
            max={Math.max(
              10_000_000,
              Math.round(inputs.currentSavingsAnnualCents * 3)
            )}
            step={dollarsToCents(500)}
            value={draft.currentSavingsAnnualCents}
            onChange={(e) =>
              patch({ currentSavingsAnnualCents: Number(e.target.value) })
            }
            className="w-full"
            aria-label="Annual savings"
          />
        </label>
      </div>

      <TrajectoryChart
        inputs={debounced}
        netWorthCents={netWorthCents}
        events={events}
        birthYear={birthYear}
        className="mt-5"
      />

      {ranked.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold text-gray-900">
            What would move the needle most
          </h3>
          <ul className="mt-2 space-y-1">
            {ranked.slice(0, 3).map((impact) => (
              <li
                key={impact.id}
                className="flex items-baseline justify-between gap-3 text-sm"
              >
                <span className="text-gray-700">
                  {impact.label}{' '}
                  <span className="text-gray-400">({impact.stepLabel})</span>
                </span>
                <span
                  className={
                    impact.delta >= 0
                      ? 'tabular-nums text-green-700'
                      : 'tabular-nums text-red-700'
                  }
                >
                  {impact.delta >= 0 ? '+' : ''}
                  {impact.delta.toFixed(2)} coverage
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500">
            Coverage compares projected savings against the years you need them
            to last at your goal retirement age. These are arithmetic
            comparisons of your own figures, not financial advice.
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDraft(inputs)}
          disabled={!isDirty || isSaving}
        >
          Reset to my plan
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!isDirty}
          isLoading={isSaving}
        >
          Set my plan based on this scenario
        </Button>
      </div>
    </div>
  );
}
